import { Box, Sheet, Stack, Typography } from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { localPoint } from "@visx/event";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { useParentSize } from "@visx/responsive";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import {
  scaleBand,
  scaleLinear,
  stack as d3Stack,
  stackOrderAppearance,
  scaleOrdinal,
  schemeTableau10,
} from "d3";
import { ApiTargetWithCount } from "griptonite-api-client";
import { uniq } from "lodash";
import sortBy from "lodash/sortBy";
import { useMemo, useState } from "react";

import { useVenueZonesByUuid } from "@/hooks/routes";
import { useTargets } from "@/hooks/targets";
import { useCurrentVenueUuid } from "@/hooks/venues";

import { RouteCircle } from "../RouteCircle";

// Types
type TooltipData = {
  grade: string;
  groups: Map<string, { name: string; targetCount: number; color: string }>;
  totalTarget: number;
};

type DataByGradeMap = Map<string, Map<string, { targetCount: number }>>;

type GroupingConfig = {
  groupKeys: string[];
  colorMap: Record<string, string>;
};

// Mock data generator
const generateMockTargets = (): ApiTargetWithCount[] => {
  const grades = ["V0", "V1", "V2", "V3", "V4", "V5", "V6", "V7", "V8"];
  const mockTargets: ApiTargetWithCount[] = [];

  grades.forEach((grade, gradeIndex) => {
    const targetCount = Math.floor(Math.random() * 15) + 5;
    const currentCount = Math.floor(Math.random() * (targetCount + 5));

    mockTargets.push({
      uuid: `mock-target-${gradeIndex}`,
      zone: null,
      gradeValue: grade,
      gradeInternal: gradeIndex * 100,
      targetCount,
      currentCount,
    } as ApiTargetWithCount);
  });

  return mockTargets;
};

const getSortedGrades = (targets: ApiTargetWithCount[]): string[] => {
  return uniq(sortBy(targets, (t) => t.gradeInternal).map((t) => t.gradeValue!));
};

const createGroupingConfig = (
  targets: ApiTargetWithCount[],
  mode: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE" | undefined,
  climbType: "BOULDER" | "SPORT",
): GroupingConfig => {
  if (mode === "BY_ZONE") {
    const uniqueZones = Array.from(new Set(targets.map((t) => t.zone || ""))) as string[];
    uniqueZones.sort();
    const colorScale = scaleOrdinal(schemeTableau10);
    const colorMap: Record<string, string> = {};
    uniqueZones.forEach((zoneUuid) => {
      colorMap[zoneUuid] = colorScale(zoneUuid);
    });
    return { groupKeys: uniqueZones, colorMap };
  }

  if (mode === "BY_ASCENT_STYLE") {
    const uniqueStyles = Array.from(
      new Set(targets.map((t) => t.ascentStyle).filter(Boolean)),
    ) as string[];
    uniqueStyles.sort();
    const colorScale = scaleOrdinal(schemeTableau10);
    const colorMap: Record<string, string> = {};
    uniqueStyles.forEach((style) => {
      colorMap[style] = colorScale(style);
    });
    return { groupKeys: uniqueStyles, colorMap };
  }

  return {
    groupKeys: ["total"],
    colorMap: { total: climbType === "BOULDER" ? "#2277C2" : "#BB6BD9" },
  };
};

const getDataByGradeMap = (
  targets: ApiTargetWithCount[],
  mode: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE" | undefined,
): DataByGradeMap => {
  const dataByGrade: DataByGradeMap = new Map();

  targets.forEach((target) => {
    const grade = target.gradeValue || "Unknown";
    if (!dataByGrade.has(grade)) {
      dataByGrade.set(grade, new Map());
    }

    const gradeData = dataByGrade.get(grade)!;
    let groupKey = "total";

    if (mode === "BY_ZONE") {
      groupKey = target.zone || "";
    } else if (mode === "BY_ASCENT_STYLE" && target.ascentStyle) {
      groupKey = target.ascentStyle;
    }

    if (!gradeData.has(groupKey)) {
      gradeData.set(groupKey, { targetCount: 0 });
    }

    const existing = gradeData.get(groupKey)!;
    existing.targetCount += target.targetCount;
  });

  return dataByGrade;
};

const prepareStackData = (grades: string[], groupKeys: string[], dataByGrade: DataByGradeMap) => {
  type StackDatum = { grade: string; [key: string]: number | string };
  const data: StackDatum[] = grades.map((grade) => {
    const gradeData = dataByGrade.get(grade);
    const obj: StackDatum = { grade };

    groupKeys.forEach((key) => {
      obj[key] = gradeData?.get(key)?.targetCount || 0;
    });

    return obj;
  });

  const stack = d3Stack<StackDatum, string>()
    .keys(groupKeys)
    .order(stackOrderAppearance)
    .value((d, key) => (typeof d[key] === "number" ? d[key] : 0));

  const series = stack(data);

  const maxValue = Math.max(
    ...data.map((d) =>
      groupKeys.reduce((sum, key) => {
        const val = d[key];
        return sum + (typeof val === "number" ? val : 0);
      }, 0),
    ),
  );

  return { stackedData: series, maxValue };
};

const prepareTooltipData = (
  grade: string,
  gradeData: Map<string, { targetCount: number }>,
  groupKeys: string[],
  mode: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE" | undefined,
  colorMap: Record<string, string>,
  zonesByUuid?: Record<string, { uuid: string; name: string }>,
): TooltipData => {
  const groups = new Map<string, { name: string; targetCount: number; color: string }>();
  let totalTarget = 0;

  groupKeys.forEach((key) => {
    const data = gradeData.get(key);
    if (data) {
      let name = key;
      if (mode === "BY_ZONE" && zonesByUuid) {
        name = zonesByUuid[key]?.name || "(No zone)";
      } else if (mode === "BY_ASCENT_STYLE") {
        if (key === "TOPROPE") name = "Toprope";
        else if (key === "LEAD") name = "Lead";
        else if (key === "AUTO_BELAY") name = "Auto belay";
      }

      groups.set(key, {
        name,
        targetCount: data.targetCount,
        color: colorMap[key],
      });

      totalTarget += data.targetCount;
    }
  });

  return { grade, groups, totalTarget };
};

const findTargetUuid = (
  targets: ApiTargetWithCount[],
  grade: string,
  mode: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE" | undefined,
  groupKey?: string,
): string | undefined => {
  const target = targets.find((t) => {
    if (t.gradeValue !== grade) return false;

    if (mode === "BY_ZONE") {
      return (t.zone || "") === groupKey;
    } else if (mode === "BY_ASCENT_STYLE" && groupKey) {
      return t.ascentStyle === groupKey;
    }

    return true;
  });

  return target?.uuid;
};

export default function TargetsGraph({
  climbType,
  showAllGrades,
  displayMockData,
}: {
  climbType: "BOULDER" | "SPORT";
  showAllGrades?: boolean;
  displayMockData?: boolean;
}) {
  const venueUuid = useCurrentVenueUuid();
  const targetsQuery = useTargets(venueUuid, climbType);
  const zonesByUuid = useVenueZonesByUuid(venueUuid, climbType);
  const navigate = useNavigate();

  // Use mock data if displayMockData prop is true
  const targets = useMemo(() => {
    if (displayMockData) {
      return generateMockTargets();
    }
    return targetsQuery.data?.targets || [];
  }, [displayMockData, targetsQuery.data?.targets]);

  const mode = displayMockData ? "GLOBAL" : (targetsQuery.data?.config.mode ?? "GLOBAL");

  // Filter targets based on showAllGrades
  const targetsFiltered = useMemo(() => {
    if (showAllGrades) {
      return targets;
    }
    return targets.filter((t) => t.currentCount !== 0 && t.targetCount !== 0);
  }, [showAllGrades, targets]);

  // Prepare data for graphing
  const grades = useMemo(() => getSortedGrades(targetsFiltered), [targetsFiltered]);

  const { groupKeys, colorMap } = useMemo(
    () => createGroupingConfig(targetsFiltered, mode, climbType),
    [targetsFiltered, mode, climbType],
  );

  const dataByGrade = useMemo(
    () => getDataByGradeMap(targetsFiltered, mode),
    [targetsFiltered, mode],
  );

  // Prepare data for d3 stack
  const { stackedData, maxValue } = useMemo(
    () => prepareStackData(grades, groupKeys, dataByGrade),
    [grades, groupKeys, dataByGrade],
  );

  // Graph dimensions
  const marginTop = 10;
  const marginLeft = maxValue > 100 ? 60 : 50;
  const marginBottom = 30;

  const {
    parentRef,
    width: parentWidth,
    height,
  } = useParentSize({
    debounceTime: 10,
  });

  const minColWidth = 60;
  const width = Math.max(parentWidth, grades.length * minColWidth);

  const xMax = width - marginLeft;
  const yMax = height - marginTop - marginBottom;

  // Scales
  const xScale = scaleBand<string>()
    .domain(grades)
    .range([0, xMax])
    .paddingInner(0.5)
    .paddingOuter(0.25);

  const yScale = scaleLinear().domain([0, maxValue]).range([yMax, 0]).nice();

  const xScaleFull = xScale.copy().paddingInner(0).paddingOuter(0);

  // Tooltip
  const [overId, setOverId] = useState<string | null>(null);
  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<TooltipData>();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
    zIndex: 1000,
  });

  const onMouseOver = (event: React.MouseEvent<SVGRectElement, MouseEvent>, grade: string) => {
    const target = event.target as SVGElement;
    const coords = target.ownerSVGElement ? localPoint(target.ownerSVGElement, event) : null;
    if (!coords) return;

    const gradeData = dataByGrade.get(grade);
    if (!gradeData) return;

    const tooltipData = prepareTooltipData(
      grade,
      gradeData,
      groupKeys,
      mode,
      colorMap,
      zonesByUuid,
    );

    setOverId(grade);
    showTooltip({
      tooltipData,
      tooltipLeft: coords.x,
      tooltipTop: coords.y,
    });
  };

  const onBarClick = (grade: string, groupKey?: string) => {
    if (displayMockData) return;

    const targetUuid = findTargetUuid(targetsFiltered, grade, mode, groupKey);
    if (targetUuid) {
      navigate({
        from: "/gym/$venueUuid/settings/targets/$climbType",
        to: "./$targetUuid",
        params: { targetUuid },
        replace: true,
      });
    }
  };

  // Background rects for hover effect
  const bgRects = (
    <g>
      {grades.map((grade, i) => {
        const isOver = overId === grade;
        return (
          <rect
            key={i}
            x={xScaleFull(grade) || 0}
            y={0}
            width={xScaleFull.bandwidth()}
            height={yMax}
            fill={isOver ? "#E5EFFF" : "black"}
            fillOpacity={isOver ? 1 : i % 2 === 0 ? 0 : 0.03}
          />
        );
      })}
    </g>
  );

  // Tooltip trigger rects - handles both hover and clicks
  const tooltipTriggers = (
    <g>
      {grades.map((grade) => (
        <rect
          key={grade}
          x={xScaleFull(grade) || 0}
          y={0}
          width={xScaleFull.bandwidth()}
          height={yMax}
          fill="transparent"
          onMouseOver={(event) => onMouseOver(event, grade)}
          onMouseMove={(event) => onMouseOver(event, grade)}
          onMouseOut={() => {
            hideTooltip();
            setOverId(null);
          }}
          onClick={(event) => {
            // Determine which bar segment was clicked based on Y coordinate
            const target = event.target as SVGElement;
            const coords = target.ownerSVGElement
              ? localPoint(target.ownerSVGElement, event)
              : null;
            if (!coords) return;

            const yPos = coords.y - marginTop;

            // Find which bar segment was clicked by checking y position
            let clickedGroupKey: string | undefined;
            const gradeData = dataByGrade.get(grade);

            if (gradeData) {
              for (const key of groupKeys) {
                const data = gradeData.get(key);
                if (data && data.targetCount > 0) {
                  // Calculate the bar segment's y bounds
                  let accumulatedValue = 0;
                  for (const k of groupKeys) {
                    const d = gradeData.get(k);
                    if (d) {
                      const bottomY = yScale(accumulatedValue);
                      accumulatedValue += d.targetCount;
                      const topY = yScale(accumulatedValue);

                      if (yPos >= topY && yPos <= bottomY && k === key) {
                        clickedGroupKey = k !== "total" ? k : undefined;
                        break;
                      }
                    }
                  }
                }
              }
            }

            onBarClick(grade, clickedGroupKey);
          }}
          strokeWidth={1}
          stroke={overId === grade ? "#2277C2" : "transparent"}
          rx={4}
          style={{ cursor: displayMockData ? "default" : "pointer" }}
        />
      ))}
    </g>
  );

  return (
    <Box
      ref={parentRef}
      sx={{
        padding: 0,
        minHeight: 200,
        height: "100%",
        overflow: "hidden",
      }}
    >
      <svg ref={containerRef} width={width} height={height}>
        <text
          textAnchor="middle"
          transform={`translate(20, ${height / 2 - marginTop}) rotate(-90)`}
          fontSize={14}
        >
          Target count
        </text>
        <GridRows
          top={marginTop}
          left={marginLeft}
          scale={yScale}
          width={xMax}
          height={yMax}
          stroke="#F1F1F4"
          strokeOpacity={1}
          strokeDasharray="4,2"
        />
        <Group top={marginTop} left={marginLeft}>
          {bgRects}
          {/* Stacked bars */}
          {stackedData.map((series) => (
            <g key={series.key} fill={colorMap[series.key]}>
              {series.map((d, i) => {
                const barHeight = yScale(d[0]) - yScale(d[1]);
                const grade = d.data.grade as string;

                return (
                  <g key={i}>
                    <rect
                      x={xScale(grade) || 0}
                      y={yScale(d[1])}
                      width={xScale.bandwidth()}
                      height={barHeight}
                      pointerEvents="none"
                    />
                    <rect
                      x={(xScale(grade) || 0) + 1}
                      y={yScale(d[1]) + 1}
                      width={Math.max(xScale.bandwidth() - 2, 0)}
                      height={Math.max(barHeight - 2, 0)}
                      strokeWidth={1}
                      strokeOpacity={0.15}
                      stroke="#000"
                      fill="transparent"
                      pointerEvents="none"
                    />
                  </g>
                );
              })}
            </g>
          ))}
        </Group>
        <AxisLeft
          top={marginTop}
          left={marginLeft}
          scale={yScale}
          tickValues={yScale.ticks().filter((tick) => Number.isInteger(tick))}
          tickFormat={yScale.tickFormat(10, ",d")}
          tickLength={6}
          tickLabelProps={() => ({
            fontSize: 11,
            textAnchor: "end",
            verticalAnchor: "middle",
            fontWeight: "bold",
          })}
          stroke="#C5C9D3"
          tickStroke="transparent"
        />
        <AxisBottom
          top={yMax + marginTop}
          left={marginLeft}
          scale={xScale}
          numTicks={grades.length}
          tickLabelProps={{
            fontSize: 11,
            textAnchor: "middle",
            fontWeight: "bold",
            fontFamily: "IBM Plex Sans",
          }}
          tickStroke="transparent"
          tickLength={4}
          stroke="#C5C9D3"
        />
        <Group top={marginTop} left={marginLeft}>
          {tooltipTriggers}
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal
          zIndex={1000}
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
          style={{
            zIndex: 1000,
            position: "absolute",
          }}
        >
          <TooltipContent tooltipData={tooltipData} mode={mode} />
        </TooltipInPortal>
      )}
    </Box>
  );
}

const TooltipContent = ({
  tooltipData,
  mode,
}: {
  tooltipData: TooltipData;
  mode?: "GLOBAL" | "BY_ZONE" | "BY_ASCENT_STYLE";
}) => {
  return (
    <Sheet variant="outlined" sx={{ borderRadius: 8, boxShadow: "lg" }}>
      <Stack sx={{ minWidth: 130, paddingBottom: 0.5 }} gap={0}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          sx={{ padding: 0.75, paddingY: 1 }}
        >
          <Typography level="body-sm" fontWeight="bold" lineHeight={1}>
            {tooltipData.grade}
          </Typography>
          <Typography level="body-sm" textColor="neutral.600" lineHeight={1}>
            {tooltipData.totalTarget}
          </Typography>
        </Stack>
        {mode !== "GLOBAL" &&
          Array.from(tooltipData.groups.values()).map((group, i) => (
            <Stack
              key={i}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              sx={{ paddingX: 1, paddingY: 0.5 }}
            >
              <Stack direction="row" gap={0.5} alignItems="center">
                <RouteCircle colors={[group.color]} style={{ width: 18, height: 18 }} />
                <Typography
                  level="body-sm"
                  sx={{
                    maxWidth: 150,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  fontWeight="bold"
                >
                  {group.name}
                </Typography>
              </Stack>
              <Typography level="body-sm" fontWeight="bold">
                {group.targetCount}
              </Typography>
            </Stack>
          ))}
      </Stack>
    </Sheet>
  );
};
